import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUsers, 
  FaUserPlus, 
  FaEdit, 
  FaTrash, 
  FaShieldAlt,
  FaCheck,
  FaTimes,
  FaSave,
  FaUserShield,
  FaKey
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordModal, setPasswordModal] = useState({ show: false, userId: null, username: '' });
  const [newPassword, setNewPassword] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    is_admin: false,
    dashboard: true,
    reminders: true,
    orders: true,
    notes: true,
    calendar: true,
    sashe: false,
    crypto: false,
    sales: true,
    tools: true,
    settings: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Nemáte oprávnenie na zobrazenie používateľov');
      } else {
        toast.error('Chyba pri načítaní používateľov');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.username || !newUser.password) {
      toast.error('Meno a heslo sú povinné');
      return;
    }

    try {
      await api.post('/users', newUser);
      toast.success('Používateľ vytvorený úspešne');
      setNewUser({
        username: '',
        password: '',
        is_admin: false,
        dashboard: true,
        reminders: true,
        orders: true,
        notes: true,
        calendar: true,
        sashe: false,
        crypto: false,
        sales: true,
        tools: true,
        settings: true
      });
      setShowAddForm(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Chyba pri vytváraní používateľa');
    }
  };

  const handleUpdatePermissions = async (userId, permissions) => {
    try {
      await api.put(`/users/${userId}/permissions`, permissions);
      toast.success('Oprávnenia aktualizované');
      fetchUsers();
      setEditingUser(null);
    } catch (error) {
      toast.error('Chyba pri aktualizácii oprávnení');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Naozaj chcete zmazať tohto používateľa?')) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`);
      toast.success('Používateľ zmazaný');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Chyba pri mazaní používateľa');
    }
  };

  const openPasswordModal = (userId, username) => {
    setPasswordModal({ show: true, userId, username });
    setNewPassword('');
  };

  const closePasswordModal = () => {
    setPasswordModal({ show: false, userId: null, username: '' });
    setNewPassword('');
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Heslo musí mať aspoň 6 znakov');
      return;
    }

    try {
      await api.put(`/users/${passwordModal.userId}/password`, { password: newPassword });
      toast.success(`Heslo pre používateľa ${passwordModal.username} bolo zmenené`);
      closePasswordModal();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Chyba pri zmene hesla');
    }
  };

  const PermissionCheckbox = ({ label, checked, onChange, disabled = false }) => (
    <label className={`permission-checkbox ${disabled ? 'disabled' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span className="checkbox-label">{label}</span>
    </label>
  );

  const UserCard = ({ user }) => {
    const [permissions, setPermissions] = useState(user.permissions);
    const isEditing = editingUser === user.id;

    const handlePermissionChange = (key, value) => {
      setPermissions({ ...permissions, [key]: value });
    };

    return (
      <motion.div
        className="user-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
      >
        <div className="user-header">
          <div className="user-info">
            <h3>
              {user.is_admin && <FaUserShield className="admin-icon" />}
              {user.username}
            </h3>
            {user.is_admin && <span className="admin-badge">Admin</span>}
          </div>
          <div className="user-actions">
            {isEditing ? (
              <>
                <button
                  className="btn-icon save"
                  onClick={() => handleUpdatePermissions(user.id, permissions)}
                  title="Uložiť"
                >
                  <FaSave />
                </button>
                <button
                  className="btn-icon cancel"
                  onClick={() => {
                    setEditingUser(null);
                    setPermissions(user.permissions);
                  }}
                  title="Zrušiť"
                >
                  <FaTimes />
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn-icon edit"
                  onClick={() => setEditingUser(user.id)}
                  title="Upraviť oprávnenia"
                >
                  <FaEdit />
                </button>
                <button
                  className="btn-icon password"
                  onClick={() => openPasswordModal(user.id, user.username)}
                  title="Zmeniť heslo"
                >
                  <FaKey />
                </button>
                <button
                  className="btn-icon delete"
                  onClick={() => handleDeleteUser(user.id)}
                  title="Zmazať"
                >
                  <FaTrash />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="permissions-grid">
          <h4>Oprávnenia:</h4>
          
          <PermissionCheckbox
            label="Admin"
            checked={permissions.is_admin}
            onChange={(e) => handlePermissionChange('is_admin', e.target.checked)}
            disabled={!isEditing}
          />
          
          <PermissionCheckbox
            label="Dashboard"
            checked={permissions.dashboard}
            onChange={(e) => handlePermissionChange('dashboard', e.target.checked)}
            disabled={!isEditing}
          />
          
          <PermissionCheckbox
            label="Pripomienky"
            checked={permissions.reminders}
            onChange={(e) => handlePermissionChange('reminders', e.target.checked)}
            disabled={!isEditing}
          />
          
          <PermissionCheckbox
            label="Objednávky"
            checked={permissions.orders}
            onChange={(e) => handlePermissionChange('orders', e.target.checked)}
            disabled={!isEditing}
          />
          
          <PermissionCheckbox
            label="Poznámky"
            checked={permissions.notes}
            onChange={(e) => handlePermissionChange('notes', e.target.checked)}
            disabled={!isEditing}
          />
          
          <PermissionCheckbox
            label="Kalendár"
            checked={permissions.calendar}
            onChange={(e) => handlePermissionChange('calendar', e.target.checked)}
            disabled={!isEditing}
          />
          
          <PermissionCheckbox
            label="Sashe-klikač"
            checked={permissions.sashe}
            onChange={(e) => handlePermissionChange('sashe', e.target.checked)}
            disabled={!isEditing}
          />

          <PermissionCheckbox
            label="Crypto Portfolio"
            checked={permissions.crypto}
            onChange={(e) => handlePermissionChange('crypto', e.target.checked)}
            disabled={!isEditing}
          />

          <PermissionCheckbox
            label="Obchod/Objednávky"
            checked={permissions.shop_orders}
            onChange={(e) => handlePermissionChange('shop_orders', e.target.checked)}
            disabled={!isEditing}
          />
          
          <PermissionCheckbox
            label="Obchod/Predaj"
            checked={permissions.sales}
            onChange={(e) => handlePermissionChange('sales', e.target.checked)}
            disabled={!isEditing}
          />
          
          <PermissionCheckbox
            label="Nástroje"
            checked={permissions.tools}
            onChange={(e) => handlePermissionChange('tools', e.target.checked)}
            disabled={!isEditing}
          />
          
          <PermissionCheckbox
            label="Nastavenia"
            checked={permissions.settings}
            onChange={(e) => handlePermissionChange('settings', e.target.checked)}
            disabled={!isEditing}
          />
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return <div className="loading">Načítavam používateľov...</div>;
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <h1><FaUsers /> Správa používateľov</h1>
        <motion.button
          className="btn btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaUserPlus /> Pridať používateľa
        </motion.button>
      </div>

      {showAddForm && (
        <motion.div
          className="add-user-form"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <h2>Nový používateľ</h2>
          <form onSubmit={handleCreateUser}>
            <div className="form-row">
              <input
                type="text"
                placeholder="Používateľské meno"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                required
              />
              <input
                type="password"
                placeholder="Heslo"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
              />
            </div>

            <div className="permissions-grid">
              <h4>Oprávnenia:</h4>
              
              <PermissionCheckbox
                label="Admin"
                checked={newUser.is_admin}
                onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })}
              />
              
              <PermissionCheckbox
                label="Dashboard"
                checked={newUser.dashboard}
                onChange={(e) => setNewUser({ ...newUser, dashboard: e.target.checked })}
              />
              
              <PermissionCheckbox
                label="Pripomienky"
                checked={newUser.reminders}
                onChange={(e) => setNewUser({ ...newUser, reminders: e.target.checked })}
              />
              
              <PermissionCheckbox
                label="Objednávky"
                checked={newUser.orders}
                onChange={(e) => setNewUser({ ...newUser, orders: e.target.checked })}
              />
              
              <PermissionCheckbox
                label="Poznámky"
                checked={newUser.notes}
                onChange={(e) => setNewUser({ ...newUser, notes: e.target.checked })}
              />
              
              <PermissionCheckbox
                label="Kalendár"
                checked={newUser.calendar}
                onChange={(e) => setNewUser({ ...newUser, calendar: e.target.checked })}
              />
              
              <PermissionCheckbox
                label="Sashe-klikač"
                checked={newUser.sashe}
                onChange={(e) => setNewUser({ ...newUser, sashe: e.target.checked })}
              />

              <PermissionCheckbox
                label="Crypto Portfolio"
                checked={newUser.crypto}
                onChange={(e) => setNewUser({ ...newUser, crypto: e.target.checked })}
              />

              <PermissionCheckbox
                label="Obchod/Predaj"
                checked={newUser.sales}
                onChange={(e) => setNewUser({ ...newUser, sales: e.target.checked })}
              />
              
              <PermissionCheckbox
                label="Nástroje"
                checked={newUser.tools}
                onChange={(e) => setNewUser({ ...newUser, tools: e.target.checked })}
              />
              
              <PermissionCheckbox
                label="Nastavenia"
                checked={newUser.settings}
                onChange={(e) => setNewUser({ ...newUser, settings: e.target.checked })}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                <FaCheck /> Vytvoriť
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowAddForm(false)}
              >
                <FaTimes /> Zrušiť
              </button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="users-grid">
        {users.map(user => (
          <UserCard key={user.id} user={user} />
        ))}
      </div>

      {users.length === 0 && (
        <div className="no-users">
          <FaUsers />
          <p>Žiadni používatelia</p>
        </div>
      )}

      {/* Password Change Modal */}
      {passwordModal.show && (
        <div className="modal-overlay" onClick={closePasswordModal}>
          <motion.div 
            className="modal-content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Zmena hesla</h2>
            <p>Zmeniť heslo pre používateľa: <strong>{passwordModal.username}</strong></p>
            
            <div className="form-group">
              <label>Nové heslo (min. 6 znakov)</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Zadajte nové heslo"
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-success"
                onClick={handleChangePassword}
              >
                <FaSave /> Uložiť
              </button>
              <button 
                className="btn btn-secondary"
                onClick={closePasswordModal}
              >
                <FaTimes /> Zrušiť
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Users;